import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { allProducts } from '../../mocks/products';
import SchemaMarkup from '../../components/base/SchemaMarkup';

const ProductDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState<'specs' | 'features'>('specs');

  // Find product from allProducts array
  const product = allProducts.find(p => p.id === id);

  const productSchema = useMemo(() => {
    if (!product) return null;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.title,
        "description": product.fullDescription || product.description,
        "image": product.images,
        "brand": {
          "@type": "Brand",
          "name": "Redsail"
        },
        "category": product.category,
        "sku": product.id,
        "manufacturer": {
          "@type": "Organization",
          "name": "Redsail Laser"
        },
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "MADLIENA Landmark Ltd"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "priceCurrency": "EUR"
          },
          "shippingDetails": {
            "@type": "OfferShippingDetails",
            "shippingDestination": {
              "@type": "DefinedRegion",
              "addressCountry": "MT"
            },
            "deliveryTime": {
              "@type": "ShippingDeliveryTime",
              "handlingTime": {
                "@type": "QuantitativeValue",
                "minValue": 14,
                "maxValue": 28,
                "unitCode": "DAY"
              }
            }
          },
          "hasMerchantReturnPolicy": {
            "@type": "MerchantReturnPolicy",
            "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
            "merchantReturnDays": 30
          }
        },
        "additionalProperty": product.specs.map(spec => ({
          "@type": "PropertyValue",
          "name": spec.label,
          "value": spec.value
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://redsaillaser-malta.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Products",
            "item": "https://redsaillaser-malta.com/products"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": product.title,
            "item": `https://redsaillaser-malta.com/product/${product.id}`
          }
        ]
      }
    ];
  }, [product]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/" className="text-emerald-500 hover:underline">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {productSchema && <SchemaMarkup schema={productSchema} />}
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-emerald-500 transition-colors">
              Home
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/products" className="text-gray-500 hover:text-emerald-500 transition-colors">
              Products
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{product.title}</span>
          </nav>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="relative aspect-square bg-white rounded-2xl overflow-hidden mb-4 shadow-sm">
                {product.badge && (
                  <span className="absolute top-4 left-4 z-10 px-4 py-1.5 bg-emerald-500 text-white text-sm font-semibold rounded-full">
                    {product.badge}
                  </span>
                )}
                <img
                  src={product.images[selectedImage]}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.images.length > 1 && (
                <div className="flex gap-3">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-24 h-24 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-emerald-500' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <span className="text-emerald-500 font-semibold text-sm tracking-wider uppercase mb-2">
                {product.subtitle}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{product.title}</h1>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">{product.fullDescription}</p>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {product.specs.slice(0, 4).map((spec, index) => (
                  <div key={index} className="bg-white rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">{spec.label}</div>
                    <div className="font-semibold text-gray-900">{spec.value}</div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="mt-auto pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-truck-line text-emerald-500"></i>
                      <span className="text-sm text-gray-600">Free Shipping</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-shield-check-line text-emerald-500"></i>
                      <span className="text-sm text-gray-600">2-Year Warranty</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4">
                  <a
                    href="tel:+35699446552"
                    className="flex-1 py-4 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-600 transition-all text-center whitespace-nowrap"
                  >
                    Request Quote
                  </a>
                  <a
                    href="https://wa.me/35699446552"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <i className="ri-whatsapp-line text-2xl text-gray-700"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 mb-8 bg-gray-100 p-1 rounded-full w-fit">
            {[
              { id: 'specs', label: 'Specifications' },
              { id: 'features', label: 'Key Features' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'specs' | 'features')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'specs' && (
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Technical Specifications</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {product.specs.map((spec, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center py-4 px-6 bg-white rounded-xl"
                  >
                    <span className="text-gray-600">{spec.label}</span>
                    <span className="font-semibold text-gray-900">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Features & Applications</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Product Highlights</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {product.description && (
                      <div className="flex items-start gap-3 py-3 px-4 bg-white rounded-xl">
                        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-emerald-500"></i>
                        </div>
                        <span className="text-gray-700">{product.description}</span>
                      </div>
                    )}
                    {product.category && (
                      <div className="flex items-start gap-3 py-3 px-4 bg-white rounded-xl">
                        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-emerald-500"></i>
                        </div>
                        <span className="text-gray-700">Category: {product.category}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Products</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {allProducts
              .filter((p) => p.id !== product.id && p.category === product.category)
              .slice(0, 3)
              .map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedProduct.images[0]}
                      alt={relatedProduct.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-emerald-500 text-sm font-medium">
                      {relatedProduct.subtitle}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mt-1 group-hover:text-emerald-600 transition-colors">
                      {relatedProduct.title}
                    </h3>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Need Help Choosing?</h2>
          <p className="text-lg text-white/90 mb-8">
            Our experts are ready to help you find the perfect laser machine for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="px-8 py-4 bg-white text-emerald-600 font-semibold rounded-full hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              Contact Sales Team
            </Link>
            <a
              href="tel:+35699446552"
              className="px-8 py-4 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-all whitespace-nowrap"
            >
              Call +356 9944 6552
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
